import * as Sentry from "@sentry/react";
import { lazy, type LazyExoticComponent } from "react";
import { navigate } from "wouter/use-browser-location";

export const routeImports = {
    "/": () => import("@/Pages/Home/CareerPlusHome"),
    "/quiz": () => import("@/Pages/Quiz"),
    "/start": () => import("@/Pages/Start"),
    "/register": () =>
        import("@/Pages/Auth/ClientRegister/ClientRegisterWithGoogle"),
    "/faq": () => import("@/Pages/FAQ"),
    "/contact": () => import("@/Pages/ContactUs"),
    "/privacy": () => import("@/Pages/Legalese/PrivacyPolicy"),
    "/terms": () => import("@/Pages/Legalese/TOS"),
    "/about": () => import("@/Pages/About"),
    "/reviews": () => import("@/Pages/Reviews/Reviews"),
    "/resources/archetypes": () => import("@/Pages/Archetypes"),
    "/coach-apply": () => import("@/Pages/CoachApply/CoachApply"),
} as const;

export type RoutePath = keyof typeof routeImports;

type RoutePageComponent<Path extends RoutePath> = Awaited<
    ReturnType<(typeof routeImports)[Path]>
>["default"];

export function lazyRoute<Path extends RoutePath>(
    path: Path,
): LazyExoticComponent<RoutePageComponent<Path>> {
    const tryLoad = async (attempt = 1) => {
        const load = routeImports[path];
        try {
            return await load();
        } catch (error) {
            Sentry.addBreadcrumb({
                message: "Failed to load bundle for route: " + path,
                level: "warning",
                data: {
                    attempt,
                },
            });
            if (attempt > 3) {
                Sentry.captureException(error);
                navigate("/page-load-error?target=" + path);
                throw error;
            }
            // Try again immediately, then 500ms delay, then 1000ms delay, then fail
            await new Promise((resolve) =>
                setTimeout(resolve, 500 * (attempt - 1)),
            );
            return await tryLoad(attempt + 1);
        }
    };
    /**
     * TypeScript cannot correlate the generic RoutePath with the matching loader's
     * return type so we have to force the type down to the route's component.
     */
    return lazy(() => tryLoad()) as LazyExoticComponent<
        RoutePageComponent<Path>
    >;
}

function findImportFunc(path: string) {
    return Object.entries(routeImports).find(
        ([route]) => path === route || path.startsWith(`${route}/`),
    )?.[1];
}

/**
 * Fires a route's import ahead of navigation. Safe to call repeatedly, with
 * absolute or relative hrefs, and with paths this SPA does not serve — an
 * unknown path is a no-op.
 */
export function preloadRoute(href: string): void {
    let path: string;
    try {
        path = new URL(href, window.location.origin).pathname;
    } catch {
        return;
    }

    const load = findImportFunc(path);
    if (!load) {
        return;
    }

    load();
}

let hasStartedIdlePreload = false;

/**
 * Fetches every remaining route chunk after the page has fully loaded, one at a
 * time during browser idle periods. Skips entirely on data-saver or 2G.
 */
export function preloadAllRoutesWhenIdle(): void {
    if (hasStartedIdlePreload) {
        return;
    }
    hasStartedIdlePreload = true;

    const { connection } = navigator as Navigator & {
        connection?: { saveData?: boolean; effectiveType?: string };
    };
    if (connection?.saveData === true) {
        return;
    }
    if (connection?.effectiveType?.endsWith("2g") === true) {
        return;
    }

    const scheduleIdle = (callback: () => void): void => {
        if (typeof globalThis.requestIdleCallback === "function") {
            globalThis.requestIdleCallback(callback);
        } else {
            globalThis.setTimeout(callback, 1500);
        }
    };

    const queue = Object.values(routeImports);
    const loadNext = async (): Promise<void> => {
        const load = queue.shift();
        if (!load) {
            return;
        }
        try {
            await load();
        } catch {
            // Same reason as preloadRoute: opportunistic, never fatal.
        }
        scheduleIdle(() => void loadNext());
    };

    const start = (): void => scheduleIdle(() => void loadNext());
    if (document.readyState === "complete") {
        start();
    } else {
        window.addEventListener("load", start, { once: true });
    }
}
